import { useEffect } from 'preact/hooks';
import type { RefObject } from 'preact';

const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Trap Tab/Shift+Tab focus inside the container referenced by `ref`.
 * Used by Drawer + ConfirmModal so keyboard users can't tab out of an
 * open overlay onto the chrome below.
 *
 * On mount, focuses the first focusable element. On unmount, restores
 * focus to whatever the user had focused before the trap engaged.
 */
export function useFocusTrap<T extends HTMLElement>(
    ref: RefObject<T>,
    active: boolean = true,
): void {
    useEffect(() => {
        if (!active) return;
        const node = ref.current;
        if (!node) return;

        const previousActive = document.activeElement as HTMLElement | null;

        const focusables = (): HTMLElement[] => {
            const out: HTMLElement[] = [];
            const list = node.querySelectorAll(FOCUSABLE);
            for (let i = 0; i < list.length; i++) {
                const el = list[i] as HTMLElement;
                if (el.hasAttribute('disabled') || el.tabIndex === -1) continue;
                out.push(el);
            }
            return out;
        };

        const all = focusables();
        if (all.length > 0) {
            all[0].focus();
        } else {
            // Make the container itself focusable so Tab has somewhere
            // to land - avoids leaking focus to body.
            node.setAttribute('tabindex', '-1');
            node.focus();
        }

        const onKey = (e: KeyboardEvent) => {
            if (e.key !== 'Tab') return;
            const list = focusables();
            if (list.length === 0) {
                e.preventDefault();
                return;
            }
            const first = list[0];
            const last = list[list.length - 1];
            const current = document.activeElement as HTMLElement | null;
            if (e.shiftKey && current === first) {
                e.preventDefault();
                last.focus();
            } else if (!e.shiftKey && current === last) {
                e.preventDefault();
                first.focus();
            }
        };

        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('keydown', onKey);
            previousActive?.focus?.();
        };
    }, [ref, active]);
}
